package pl.nickers.match3

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.nickers.match3.ecs.*
import kotlin.random.Random

/** Default board size (columns and rows) for arcade games and levels that omit a size. */
const val GRID_SIZE = 7

/** Inclusive bounds the dev-tools board-size editor clamps width/height to. */
const val MIN_BOARD_DIMENSION = 3
const val MAX_BOARD_DIMENSION = 12

const val SWAP_DURATION_MS = 300
const val FUSE_DURATION_MS = 300
const val LASER_DURATION_MS = 300
const val EFFECTS_DURATION_MS = 400
const val FALL_STEP_DURATION_MS = 100
const val UI_FLIGHT_DURATION_MS = 200
const val SWIRL_FADE_DURATION_MS = 300
const val SWIRL_VACUUM_DURATION_MS = 900
const val ROW_BOMB_STEP_DELAY_MS = 100L

const val MOVE_HISTORY_MAX = 25

/**
 * String tokens used by [GameViewModel.initialGrid] to encode entity types in
 * level definitions. The tokens are either canonical [PieceIds] (e.g.
 * `"ember_seed"`, `"bomb"`, `"laser_horizontal"`), or one of the layout sentinels
 * declared on [GridTokens]. The parser lives in [parseGridToken].
 */
object GridTokens {
    /** A permanent hole (not part of the playable grid). */
    const val HOLE = "hole"
    /** A playable cell that starts with no entity in it (e.g. a teleport exit). */
    const val EMPTY = "empty"
    /** An ice cube whose initial life is 1 (one hit and it cracks). */
    const val ICE_CUBE_LIFE_1 = "ice_cube_life_1"
    /**
     * Prefix marking the piece in this cell as cursed (E5), e.g. `"cursed:moon_crystal"`.
     * Terrain tokens compose in front of it: `"purified_rune:cursed:moon_crystal"` seeds
     * a rune cell holding a cursed Moon Crystal, `"void_puddle"` alone seeds an empty
     * puddle cell. See [GameViewModel.createEntitiesForToken].
     */
    const val CURSED_PREFIX = "cursed:"
}

enum class DevEntityType {
    JELLY_1, JELLY_2, JELLY_3, JELLY_4, JELLY_5, JELLY_6,
    BOMB, CROSS_BOMB, LASER_H, LASER_V, COLOURFUL_SWIRL, ICE_CUBE, SPAWN_POINT,
}

/**
 * Captures the augmentation applied to a seeded test level (see
 * [GameViewModel.prepareAsTestLevel]) so it can be replayed verbatim when the
 * board is reset.
 */
private data class TestLevelSetup(
    val moveLimit: Int?,
    val teleports: List<TeleportLinkSpec>,
    val disableSpawn: Boolean,
    val goals: List<LevelGoalConfig>,
    val familiar: FamiliarType?,
)

private val VALID_PHASES = setOf(GamePhase.INPUT, GamePhase.ANIMATE, GamePhase.GRAVITY, GamePhase.RESOLVE)

class GameViewModel(
    private val catalog: EntityCatalog = EntityCatalog.default(),
    private val initialGrid: List<List<String>>? = null,
    private val storage: GameStorage = GameStorage(),
    private val forceNewGame: Boolean = false,
    private val journeyService: JourneyService? = null,
    private val livesService: LivesService? = null,
    /**
     * Gem wallet (E18). Null in arcade/test contexts that never earn gems; when
     * present, winning a journey level for the first time pays the first-win
     * bonus and the in-game toolbar shows the live balance. All gem arithmetic
     * stays inside [WalletService] — this class only reports the win.
     */
    private val wallet: WalletService? = null,
    private val levelConfig: LevelConfig? = null,
    private val enableUiFlights: Boolean = true,
    private val isTestLevel: Boolean = false,
    /**
     * Data-driven piece definitions. Derived from the same [catalog] that feeds
     * [SpawnSystem], so production reads a single source of truth: the JSON piece
     * set loaded by `EntityCatalog.load()`. Tests pass [EntityCatalog.default()],
     * whose wrapped piece set is `PieceSetLoader.default()` — the only place that
     * hand-built fallback is still consulted.
     */
    private val pieceSet: PieceSet = catalog.pieceSet,
    /** Source of all board randomness (spawns, special-tile placement). Inject a seeded instance for deterministic tests. */
    private val random: Random = Random.Default,
) {
    private val currentJourneyLevelId: Int = levelConfig?.id ?: -1
    /**
     * Remembers the augmentation applied to a seeded test level so [resetBoard]
     * ("Try Again") can replay it. Null for journey/arcade games, whose config is
     * re-applied by [initializeGrid] directly.
     */
    private var testLevelSetup: TestLevelSetup? = null

    val audioPlayer = AudioPlayer()

    private val inputSystem = InputSystem(pieceSet = pieceSet)
    private val swapResolveSystem = SwapResolveSystem(pieceSet = pieceSet)
    private val bombFuseSystem = BombFuseSystem()
    private val laserResolveSystem = LaserResolveSystem(pieceSet = pieceSet)
    private val fallResolveSystem = FallResolveSystem()
    private val effectResolveSystem = EffectResolveSystem(pieceSet = pieceSet)
    private val spawnSystem = SpawnSystem(random = random, catalog = catalog)
    private val matchDetectionSystem = MatchDetectionSystem(random = random, catalog = catalog, pieceSet = pieceSet)
    private val iceDamageSystem = IceDamageSystem(random = random, catalog = catalog, pieceSet = pieceSet)
    private val gravitySystem = GravitySystem()
    private val ghostBombSystem = GhostBombSystem()
    private val gameLoopSystem = GameLoopSystem()
    private val matchNeighbourCleanupSystem = MatchNeighbourCleanupSystem()
    private val familiarSystem = FamiliarSystem(random = random, pieceSet = pieceSet)
    private val curseSystem = CurseSystem(pieceSet = pieceSet)
    private val tutorialSystem = TutorialSystem(pieceSet = pieceSet)
    private val renderSystem = RenderSystem(catalog = catalog, pieceSet = pieceSet)
    private val audioSystem = AudioSystem(audioPlayer)

    internal val world = World {
        with(inputSystem)
        with(fallResolveSystem)
        with(swapResolveSystem)
        with(bombFuseSystem)
        with(laserResolveSystem)
        with(effectResolveSystem)
        with(gravitySystem)
        // CurseSystem must precede MatchDetectionSystem: both gate on the stable-board
        // MATCH_DETECT tick, and the curse must land before goals are counted.
        with(curseSystem)
        with(matchDetectionSystem)
        with(iceDamageSystem)
        with(spawnSystem)
        with(ghostBombSystem)
        with(familiarSystem)
        with(gameLoopSystem)
        with(matchNeighbourCleanupSystem)
        // Runs after GameLoopSystem so it observes this tick's final board.phase, and
        // before RenderSystem so the tutorial highlight it reads is up to date.
        with(tutorialSystem)
        with(renderSystem)
        with(audioSystem)
    }

    private var hadValidMove: Boolean = true

    /** Guards star recording so a win is scored exactly once while the dialog stays open. */
    private var winStarsRecorded: Boolean = false
    private var lastWinStars: Int = 0
    private var lastWinIsNewBest: Boolean = false

    private var musicStarted: Boolean = false
    private var firstMoveMade: Boolean = false
    private var lossFinalized: Boolean = false

    private val posMapper = world.mapper<GridPositionComponent>()
    private val typeMapper = world.mapper<JellyTypeComponent>()
    private val powerUpTypeMapper = world.mapper<PowerUpTypeComponent>()
    private val powerUpUsesMapper = world.mapper<PowerUpUsesComponent>()
    private val iceCubeMapper = world.mapper<IceCubeComponent>()
    private val bodyImageMapper = world.mapper<BodyImageComponent>()
    private val jellyFaceMapper = world.mapper<JellyFaceComponent>()
    private val draggableMapper = world.mapper<DraggableComponent>()
    private val fusedBombMapper = world.mapper<FusedBombComponent>()
    private val activatedSwirlMapper = world.mapper<ActivatedSwirlComponent>()
    private val hitByAreaEffectMapper = world.mapper<HitByAreaEffectComponent>()

    private var boardEntity: Int = -1

    var state by mutableStateOf(GameState(grid = emptyList()))
        private set

    var showDebugOverlay by mutableStateOf(false)
        private set

    fun toggleDebugOverlay() {
        showDebugOverlay = !showDebugOverlay
    }

    var isAnimating by mutableStateOf(false)
        private set

    private var inputMode: InputMode = InputMode.SWAP
    private var rowBombInFlight: Boolean = false
    private var teleportLinkingDestination: GridPos? = null

    var highScore by mutableStateOf(storage.highScore)
        private set

    /**
     * Live gem balance for the in-game HUD (E18/F18.4). Reads through to
     * [WalletService]; a stand-in empty flow keeps the UI contract identical when
     * no wallet is attached (unit tests, previews).
     */
    val gemBalance: StateFlow<Int> = wallet?.balance ?: MutableStateFlow(0).asStateFlow()

    /**
     * Whether a gem wallet is attached; the HUD hides the chip when it is not
     * (unit tests and previews construct the ViewModel without a wallet).
     */
    val hasWallet: Boolean = wallet != null

    var canUndoMove by mutableStateOf(false)
        private set

    var canRedoMove by mutableStateOf(false)
        private set

    private val moveHistory = MoveHistory(world) { newBoardEntityId ->
        boardEntity = newBoardEntityId
        ensureBoardConfiguration()
        normalizeLoadedBoard()
        processWorld()
    }

    private var frameCount = 0
    private val phaseHistory = ArrayDeque<String>()
    private var lastTrackedPhase = ""

    init {
        audioPlayer.initialize()
        val savedSnapshot = if (initialGrid == null && !forceNewGame && levelConfig == null) storage.loadSnapshot() else null
        if (savedSnapshot != null) {
            world.restoreFrom(savedSnapshot)
            boardEntity = world.getEntitiesForAspect(Aspect.all(BoardStateComponent::class)).first()
            ensureBoardConfiguration()
        } else {
            boardEntity = world.createEntity()
            val (initialWidth, initialHeight) = resolveInitialDimensions()
            world.addComponent(
                boardEntity,
                BoardStateComponent(width = initialWidth, height = initialHeight, phase = GamePhase.INPUT),
            )
            world.addComponent(boardEntity, BoardSpawnControlComponent())
            initializeGrid()
        }
        // Attached fresh on every construction of a level-1 GameViewModel — not gated on
        // any "seen it before" flag, so the tutorial shows on every play of level 1,
        // including replays, per issue #110. TutorialSystem owns it from here on.
        if (levelConfig?.id == 1 && !isTestLevel) {
            world.addComponent(boardEntity, TutorialComponent(startScore = boardState()?.score ?: 0))
        }
        normalizeLoadedBoard()
        processWorld()
    }

    fun onCleared() {
        audioPlayer.release()
    }

    suspend fun exportToFile(): Boolean {
        val json = Json.encodeToString(world.snapshot())
        return exportGameToFile(json)
    }

    suspend fun importFromFile(): Boolean {
        val json = loadGameFromFile() ?: return false
        return try {
            val snapshot = Json.decodeFromString<WorldSnapshot>(json)
            world.getAllEntityIds().toList().forEach { world.deleteEntity(it) }
            world.restoreFrom(snapshot)
            boardEntity = world.getEntitiesForAspect(Aspect.all(BoardStateComponent::class)).first()
            ensureBoardConfiguration()
            normalizeLoadedBoard()
            clearMoveHistory()
            processWorld()
            true
        } catch (_: Exception) {
            false
        }
    }

    fun undoMove() {
        if (isAnimating || !moveHistory.canUndo) return
        moveHistory.undo()
        syncMoveHistoryFlags()
    }

    fun redoMove() {
        if (isAnimating || !moveHistory.canRedo) return
        moveHistory.redo()
        syncMoveHistoryFlags()
    }

    fun setBackgroundImage(imageBase64: String) {
        if (isAnimating) return
        val existing = world.getEntitiesForAspect(
            Aspect.all(BackgroundImageComponent::class),
        ).firstOrNull()
        if (existing != null) {
            world.addComponent(existing, BackgroundImageComponent(imageBase64))
        } else {
            val entity = world.createEntity()
            world.addComponent(entity, BackgroundImageComponent(imageBase64))
        }
        processWorld()
    }

    fun removeBackgroundImage() {
        if (isAnimating) return
        world.getEntitiesForAspect(Aspect.all(BackgroundImageComponent::class))
            .forEach { world.deleteEntity(it) }
        processWorld()
    }

    fun setDisableNewJellySpawn(disabled: Boolean) {
        if (isAnimating) return
        val spawnControl = ensureBoardConfiguration()
        if (spawnControl.disableNewJellySpawn == disabled) return
        spawnControl.disableNewJellySpawn = disabled
        processWorld()
    }

    fun forceUnfreeze() {
        val board = boardState() ?: return
        board.phase = GamePhase.INPUT
        board.currentSubphase = null
        board.didWork = false
        // Clear all in-flight animation components to prevent stuck state
        removeAllOf(FusedBombComponent::class)
        removeAllOf(ExplodingComponent::class)
        removeAllOf(FullBoardExplosionComponent::class)
        removeAllOf(ActivatedLaserComponent::class)
        removeAllOf(SwappingComponent::class)
        removeAllOf(FallingComponent::class)
        removeAllOf(TeleportingComponent::class)
        processWorld()
    }

    private fun normalizeLoadedBoard() {
        val board = boardState() ?: return
        // Snapshots saved before the 4-phase migration may have stale legacy phase names
        // that no longer exist. Silently reset to INPUT so the game continues cleanly.
        if (board.phase !in VALID_PHASES) {
            board.phase = GamePhase.INPUT
            board.currentSubphase = null
        }
    }

    fun resetBoard() {
        if (isAnimating) return
        clearMoveHistory()
        winStarsRecorded = false
        lastWinStars = 0
        lastWinIsNewBest = false
        boardState()?.score = 0
        deleteAllFlightEntities()
        firstMoveMade = false
        lossFinalized = false
        initializeGrid()
        // For seeded test levels, initializeGrid re-seeds the grid but returns before
        // applying goals/move-limit; replay the saved setup so "Try Again" restores the
        // move counter and objectives, not just the board layout.
        testLevelSetup?.let { applyTestLevelSetup(it) }
        processWorld()
    }

    /**
     * Dev tool: resizes the board to [newWidth] × [newHeight] (columns × rows),
     * preserving pieces that still fit. Cells outside the new bounds (and their
     * holes) are dropped; cells newly inside the bounds are filled with random
     * pieces. Above-board spawn points are rebuilt to span the new width.
     */
    fun setBoardSize(newWidth: Int, newHeight: Int) {
        if (isAnimating) return
        val board = boardState() ?: return
        val w = newWidth.coerceIn(MIN_BOARD_DIMENSION, MAX_BOARD_DIMENSION)
        val h = newHeight.coerceIn(MIN_BOARD_DIMENSION, MAX_BOARD_DIMENSION)
        if (w == board.width && h == board.height) return
        clearMoveHistory()
        deleteAllFlightEntities()

        // Clip: delete any on-board entity that falls outside the new bounds.
        world.getEntitiesForAspect(Aspect.all(GridPositionComponent::class)).toList().forEach { id ->
            val pos = posMapper[id] ?: return@forEach
            if (pos.row >= h || pos.col >= w) world.deleteEntity(id)
        }
        // Drop holes outside the new bounds and stamp the new dimensions.
        val clippedHoles = board.holes.filter { (r, c) -> r in 0 until h && c in 0 until w }.toSet()
        world.addComponent(boardEntity, board.copy(width = w, height = h, holes = clippedHoles))

        // Rebuild the invisible above-board spawn points so they span the new width.
        world.getEntitiesForAspect(Aspect.all(SpawnPointComponent::class, GridPositionComponent::class))
            .toList().forEach { id ->
                val pos = posMapper[id]
                if (pos != null && pos.row < 0) world.deleteEntity(id)
            }
        for (col in 0 until w) {
            world.createSpawnPointEntity(row = -1, col = col, visible = false)
        }

        // Extend: fill cells that are now in-bounds and empty with fresh random pieces.
        for (row in 0 until h) {
            for (col in 0 until w) {
                if (world.isHole(row, col)) continue
                if (findEntityAt(row, col) != null) continue
                world.createRandomBoardEntity(row = row, col = col, random = random, catalog = catalog)
            }
        }
        eliminateInitialMatches()
        processWorld()
    }

    /**
     * Augments a board seeded via [initialGrid] so it is fully playable from the
     * "Test levels" UI: adds the invisible above-board spawn points that
     * [SpawnSystem] uses to refill from the top, applies a default goal, and
     * grants the standard power-ups. Test code that wants the bare seeded board
     * does not call this.
     */
    fun prepareAsTestLevel(
        moveLimit: Int? = null,
        teleports: List<TeleportLinkSpec> = emptyList(),
        disableSpawn: Boolean = false,
        goals: List<LevelGoalConfig> = emptyList(),
        familiar: FamiliarType? = null,
    ) {
        if (initialGrid == null) return
        val setup = TestLevelSetup(
            moveLimit = moveLimit,
            teleports = teleports,
            disableSpawn = disableSpawn,
            goals = goals,
            familiar = familiar,
        )
        testLevelSetup = setup
        applyTestLevelSetup(setup)
        processWorld()
    }

    /**
     * Applies the seeded-level augmentation described by [setup]: above-board spawn
     * points (unless spawning is disabled), collection goals, the move limit,
     * teleport links, and the standard power-ups. Idempotent — safe to replay on
     * [resetBoard] after [initializeGrid] has re-seeded the grid.
     */
    private fun applyTestLevelSetup(setup: TestLevelSetup) {
        if (setup.disableSpawn) {
            ensureBoardConfiguration().disableNewJellySpawn = true
        } else {
            for (col in 0 until boardWidth()) {
                world.createSpawnPointEntity(row = -1, col = col, visible = false)
            }
        }
        applyGoalConfigs(setup.goals.ifEmpty { null })
        if (setup.moveLimit != null && setup.moveLimit > 0) {
            world.addComponent(
                boardEntity,
                MoveLimitComponent(totalMoves = setup.moveLimit, remainingMoves = setup.moveLimit),
            )
        }
        setup.teleports.forEach { spec ->
            world.createTeleportTargetEntity(
                destRow = spec.destRow,
                destCol = spec.destCol,
                sourceRow = spec.srcRow,
                sourceCol = spec.srcCol,
            )
        }
        setup.familiar?.let { ensureFamiliarEntity(it) }
        initializePowerUps()
    }

    fun replaceSelectedEntity(type: DevEntityType) {
        if (isAnimating) return
        val selectedPos = state.selected ?: return
        clearMoveHistory()
        val existingEntity = findEntityAt(selectedPos.row, selectedPos.col)
        if (existingEntity != null) {
            world.deleteEntity(existingEntity)
        }
        when (type) {
            DevEntityType.JELLY_1 -> world.createJellyEntity(selectedPos.row, selectedPos.col, 1, random)
            DevEntityType.JELLY_2 -> world.createJellyEntity(selectedPos.row, selectedPos.col, 2, random)
            DevEntityType.JELLY_3 -> world.createJellyEntity(selectedPos.row, selectedPos.col, 3, random)
            DevEntityType.JELLY_4 -> world.createJellyEntity(selectedPos.row, selectedPos.col, 4, random)
            DevEntityType.JELLY_5 -> world.createJellyEntity(selectedPos.row, selectedPos.col, 5, random)
            DevEntityType.JELLY_6 -> world.createJellyEntity(selectedPos.row, selectedPos.col, 6, random)
            DevEntityType.BOMB -> world.createBombEntity(selectedPos.row, selectedPos.col, random)
            DevEntityType.CROSS_BOMB -> world.createCrossBombEntity(selectedPos.row, selectedPos.col, random)
            DevEntityType.LASER_H -> world.createLaserEntity(selectedPos.row, selectedPos.col, LaserDirection.HORIZONTAL)
            DevEntityType.LASER_V -> world.createLaserEntity(selectedPos.row, selectedPos.col, LaserDirection.VERTICAL)
            DevEntityType.COLOURFUL_SWIRL -> world.createColourfulSwirlEntity(selectedPos.row, selectedPos.col, jellyType = random.nextInt(1, 7))
            DevEntityType.ICE_CUBE -> world.createIceCubeEntity(selectedPos.row, selectedPos.col)
            DevEntityType.SPAWN_POINT -> world.createSpawnPointEntity(selectedPos.row, selectedPos.col)
        }
        processWorld()
    }

    fun deleteSelectedEntity() {
        if (isAnimating) return
        val selectedPos = state.selected ?: return
        clearMoveHistory()
        val entity = findEntityAt(selectedPos.row, selectedPos.col) ?: return
        world.deleteEntity(entity)
        processWorld()
    }

    fun toggleHole(row: Int, col: Int) {
        if (isAnimating) return
        val board = boardState() ?: return
        clearMoveHistory()
        if (world.isHole(row, col)) {
            val newHoles = board.holes - (row to col)
            world.addComponent(boardEntity, board.copy(holes = newHoles))
            world.createRandomBoardEntity(row = row, col = col, random = random, catalog = catalog)
        } else {
            val entity = findEntityAt(row, col)
            if (entity != null) world.deleteEntity(entity)
            world.addComponent(boardEntity, board.copy(holes = board.holes + (row to col)))
        }
        processWorld()
    }

    fun setSelectedIceCubeLife(life: Int) {
        if (isAnimating) return
        val selectedPos = state.selected ?: return
        val entity = findEntityAt(selectedPos.row, selectedPos.col) ?: return
        val components = world.getAllComponentsForEntity(entity)
        if (components.none { it is IceCubeComponent }) return
        val clamped = life.coerceIn(1, 3)
        world.addComponent(entity, IceCubeComponent(life = clamped))
        world.addComponent(entity, BodyImageComponent(image = "ice_$clamped.png"))
        processWorld()
    }

    fun setSelectedLaserDirection(direction: LaserDirection) {
        if (isAnimating) return
        val selectedPos = state.selected ?: return
        val entity = findEntityAt(selectedPos.row, selectedPos.col) ?: return
        // Only laser entities accept a direction change; reject anything else.
        if (world.familyOf(entity, pieceSet) != Family.LASER) return
        val newPieceId = when (direction) {
            LaserDirection.HORIZONTAL -> PieceIds.LASER_HORIZONTAL
            LaserDirection.VERTICAL -> PieceIds.LASER_VERTICAL
        }
        world.addComponent(entity, PieceIdComponent(newPieceId))
        processWorld()
    }

    /**
     * Begins a two-step teleport-linking gesture: the next cell tap will be
     * interpreted as the source for a teleport target marker anchored at [dest].
     * Cancels by selecting [dest] itself or via [cancelTeleportLinking].
     */
    fun beginTeleportLinking(dest: GridPos) {
        if (isAnimating) return
        clearMoveHistory()
        teleportLinkingDestination = dest
        inputMode = InputMode.TELEPORT_LINKING
        removeAllOf(SelectedComponent::class)
        processWorld()
    }

    fun cancelTeleportLinking() {
        if (inputMode != InputMode.TELEPORT_LINKING) return
        teleportLinkingDestination = null
        inputMode = InputMode.SWAP
        syncState()
    }

    /**
     * Completes the gesture started by [beginTeleportLinking] by creating a
     * teleport target marker at the previously stored destination, pulling from
     * [source]. Tapping the destination itself cancels.
     */
    fun confirmTeleportLinkingSource(source: GridPos) {
        if (inputMode != InputMode.TELEPORT_LINKING) return
        val dest = teleportLinkingDestination ?: run {
            cancelTeleportLinking()
            return
        }
        if (source == dest) {
            cancelTeleportLinking()
            return
        }
        // Replace any existing marker at this destination so the second tap rebinds it.
        world.removeTeleportTargetAt(dest.row, dest.col)
        world.createTeleportTargetEntity(
            destRow = dest.row, destCol = dest.col,
            sourceRow = source.row, sourceCol = source.col,
        )
        teleportLinkingDestination = null
        inputMode = InputMode.SWAP
        processWorld()
    }

    fun removeTeleportTargetAtPos(pos: GridPos) {
        if (isAnimating) return
        val removed = world.removeTeleportTargetAt(pos.row, pos.col)
        if (removed > 0) {
            clearMoveHistory()
            processWorld()
        }
    }

    fun hasTeleportTargetAt(pos: GridPos): Boolean =
        world.teleportSourceAt(pos.row, pos.col) != null

    fun setSpawnPointVisible(visible: Boolean) {
        if (isAnimating) return
        val selectedPos = state.selected ?: return
        val entity = findEntityAt(selectedPos.row, selectedPos.col) ?: return
        val spawnPoint = world.getComponent(entity, SpawnPointComponent::class) ?: return
        world.addComponent(entity, spawnPoint.copy(visible = visible))
        processWorld()
    }

    fun addPowerUpUse(type: PowerUpType) {
        val existingEntity = world.getEntitiesForAspect(
            Aspect.all(PowerUpTypeComponent::class, PowerUpUsesComponent::class),
        ).firstOrNull { entity ->
            powerUpTypeMapper[entity]?.type == type
        }
        if (existingEntity != null) {
            val currentUses = powerUpUsesMapper[existingEntity]?.uses ?: 0
            powerUpUsesMapper.set(existingEntity, PowerUpUsesComponent(currentUses + 1))
        } else {
            world.createPowerUpEntityByPieceId(pieceIdForPowerUpType(type), uses = 1)
        }
        processWorld()
    }

    fun useBombPowerUp() {
        if (isAnimating || isLost()) return
        val powerUpEntity = world.getEntitiesForAspect(
            Aspect.all(PowerUpTypeComponent::class, PowerUpUsesComponent::class),
        ).firstOrNull { entity ->
            powerUpTypeMapper[entity]?.type == PowerUpType.BOMB &&
                (powerUpUsesMapper[entity]?.uses ?: 0) > 0
        } ?: return

        val currentUses = powerUpUsesMapper[powerUpEntity]?.uses ?: return

        val candidates = world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, JellyTypeComponent::class),
        ).filter { entity ->
            val type = typeMapper[entity]?.type ?: return@filter false
            type > 0 && world.isFamily(entity, pieceSet, Family.JELLY)
        }
        if (candidates.isEmpty()) return

        val target = candidates.random(random)
        val pos = posMapper[target] ?: return

        recordStateBeforePlayerMove()
        world.deleteEntity(target)
        world.createBombEntity(pos.row, pos.col, random)
        powerUpUsesMapper.set(powerUpEntity, PowerUpUsesComponent(currentUses - 1))
        if (enableUiFlights) {
            val flightEntity = world.createEntity()
            world.addComponent(
                flightEntity,
                BombFlightComponent(
                    targetRow = pos.row,
                    targetCol = pos.col,
                    powerUpEntityId = powerUpEntity,
                ),
            )
        }
        processWorld()
    }

    fun clearBombPowerUpFlight() {
        world.getEntitiesForAspect(Aspect.all(BombFlightComponent::class))
            .forEach { world.deleteEntity(it) }
        syncState()
    }

    private fun rowBombPowerUpEntity(): Int? = world.getEntitiesForAspect(
        Aspect.all(PowerUpTypeComponent::class, PowerUpUsesComponent::class),
    ).firstOrNull { entity ->
        powerUpTypeMapper[entity]?.type == PowerUpType.ROW_BOMB &&
            (powerUpUsesMapper[entity]?.uses ?: 0) > 0
    }

    private fun targetedRemovePowerUpEntity(): Int? = world.getEntitiesForAspect(
        Aspect.all(PowerUpTypeComponent::class, PowerUpUsesComponent::class),
    ).firstOrNull { entity ->
        powerUpTypeMapper[entity]?.type == PowerUpType.TARGETED_REMOVE &&
            (powerUpUsesMapper[entity]?.uses ?: 0) > 0
    }

    fun toggleRowBombPowerUp() {
        if (isAnimating || rowBombInFlight || isLost()) return
        val armed = inputMode == InputMode.ROW_BOMB_TARGETING
        if (!armed && rowBombPowerUpEntity() == null) return
        inputMode = if (armed) InputMode.SWAP else InputMode.ROW_BOMB_TARGETING
        if (inputMode == InputMode.ROW_BOMB_TARGETING) {
            // Arming clears any pending swap selection so the click that follows
            // is unambiguously a power-up target, not the second half of a swap.
            removeAllOf(SelectedComponent::class)
            processWorld()
        } else {
            syncState()
        }
    }

    fun toggleTargetedRemovePowerUp() {
        if (isAnimating || isLost()) return
        val armed = inputMode == InputMode.TARGETED_REMOVE_TARGETING
        if (!armed && targetedRemovePowerUpEntity() == null) return
        inputMode = if (armed) InputMode.SWAP else InputMode.TARGETED_REMOVE_TARGETING
        if (inputMode == InputMode.TARGETED_REMOVE_TARGETING) {
            removeAllOf(SelectedComponent::class)
            processWorld()
        } else {
            syncState()
        }
    }

    fun useTargetedRemovePowerUp(row: Int, col: Int) {
        if (isAnimating || isLost()) return
        val powerUpEntity = targetedRemovePowerUpEntity() ?: run {
            inputMode = InputMode.SWAP
            syncState()
            return
        }
        val entity = findEntityAt(row, col) ?: run {
            syncState()
            return
        }
        val isSpawnPoint = world.hasComponent(entity, SpawnPointComponent::class)
        if (isSpawnPoint) {
            syncState()
            return
        }

        val currentUses = powerUpUsesMapper[powerUpEntity]?.uses ?: return
        recordStateBeforePlayerMove()
        powerUpUsesMapper.set(powerUpEntity, PowerUpUsesComponent(currentUses - 1))
        inputMode = InputMode.SWAP

        val board = boardState() ?: return

        val family = world.familyOf(entity, pieceSet)
        val isBomb = family == Family.BOMB || family == Family.CROSS_BOMB
        val isLaser = family == Family.LASER
        val isSwirl = family == Family.SWIRL
        val isIce = iceCubeMapper.has(entity)

        when {
            isBomb -> {
                fusedBombMapper.set(entity, FusedBombComponent())
                board.phase = GamePhase.ANIMATE
            }
            isLaser -> {
                val dir = world.laserDirectionOf(entity, pieceSet) ?: return
                world.addComponent(entity, ActivatedLaserComponent(directions = setOf(dir)))
                board.phase = GamePhase.ANIMATE
            }
            isSwirl -> {
                fusedBombMapper.set(entity, FusedBombComponent())
                activatedSwirlMapper.set(
                    entity,
                    ActivatedSwirlComponent(targetPieceId = world.mostCommonJellyPieceId(pieceSet) ?: PieceIds.EMBER_SEED),
                )
                board.phase = GamePhase.ANIMATE
            }
            isIce -> {
                // Stamp the state marker; IceDamageSystem is phase-agnostic and will damage
                // the ice on the next world.process tick. Keep phase=INPUT so cascading
                // matches elsewhere on the board are not falsely triggered by this power-up.
                hitByAreaEffectMapper.set(entity, HitByAreaEffectComponent())
            }
            else -> {
                world.scoreDeleteAndCheckGoals(setOf(entity), board)
                board.phase = GamePhase.GRAVITY
            }
        }

        processWorld()
    }

    suspend fun useRowBombPowerUp(target: GridPos) {
        if (rowBombInFlight || isLost()) return
        val powerUpEntity = rowBombPowerUpEntity() ?: run {
            inputMode = InputMode.SWAP
            syncState()
            return
        }
        val currentUses = powerUpUsesMapper[powerUpEntity]?.uses ?: return
        rowBombInFlight = true
        inputMode = InputMode.SWAP
        recordStateBeforePlayerMove()
        powerUpUsesMapper.set(powerUpEntity, PowerUpUsesComponent(currentUses - 1))
        syncState()
        try {
            val width = boardWidth()
            for (col in 0 until width) {
                applyRowBombColumnStep(target.row, col)
                if (col < width - 1) delay(ROW_BOMB_STEP_DELAY_MS)
            }
        } finally {
            rowBombInFlight = false
            syncState()
        }
    }

    internal fun applyRowBombColumnStep(targetRow: Int, col: Int) {
        val board = boardState() ?: return
        val rows = listOf(targetRow - 1, targetRow, targetRow + 1)
            .filter { it in 0 until board.height }
        val targets = rows.mapNotNull { r -> world.findEntityAt(r, col) }.toSet()
        if (targets.isNotEmpty()) {
            val toRemove = resolveAreaOfEffect(
                world = world,
                targets = targets,
                pieceSet = pieceSet,
            )
            if (toRemove.isNotEmpty()) {
                world.scoreDeleteAndCheckGoals(toRemove, board)
                // Route to GRAVITY so the holes get filled before the next RESOLVE cycle.
                board.phase = GamePhase.GRAVITY
            }
        }
        processWorld()
    }

    fun dismissGoalJellyFlight(entityId: Int) {
        if (world.entityExists(entityId)) {
            world.deleteEntity(entityId)
        }
        syncState()
    }

    fun dismissFamiliarFlight(entityId: Int) {
        if (world.entityExists(entityId)) {
            world.deleteEntity(entityId)
        }
        syncState()
    }

    fun onCellClick(pos: GridPos) {
        if (isAnimating || rowBombInFlight || isLost()) return
        if (inputMode != InputMode.SWAP) {
            // While a non-swap mode is active, taps belong to that mode (e.g. row-bomb
            // targeting). The UI is responsible for routing taps to the appropriate
            // mode-specific handler — this guard is defensive in case it doesn't.
            return
        }
        ensureMusicStarted()
        if (inputSystem.wouldClickStartSwap(pos.row, pos.col) ||
            inputSystem.wouldDoubleClickActivate(pos.row, pos.col)) {
            recordStateBeforePlayerMove()
            firstMoveMade = true
            decrementMoveLimit()
        }
        inputSystem.enqueueClick(pos.row, pos.col)
        processWorld()
    }

    fun onDragSwap(from: GridPos, to: GridPos) {
        if (isAnimating || isLost()) return
        ensureMusicStarted()
        if (inputSystem.wouldDragStartSwap(from.row, from.col, to.row, to.col)) {
            recordStateBeforePlayerMove()
            firstMoveMade = true
            decrementMoveLimit()
        }
        inputSystem.enqueueDragSwap(from.row, from.col, to.row, to.col)
        processWorld()
    }

    private fun ensureMusicStarted() {
        if (!musicStarted) {
            musicStarted = true
            audioPlayer.startMusic()
        }
    }

    fun onSwapAnimationFinished() {
        removeAllOf(SwappingComponent::class)
        processWorld()
    }

    fun onFallAnimationFinished() {
        removeAllOf(FallingComponent::class)
        removeAllOf(TeleportingComponent::class)
        processWorld()
    }

    fun onFuseAnimationFinished() {
        // Guard: no-op if no fused bomb in flight (e.g. drainAnimations calls multiple callbacks).
        if (world.getEntitiesForAspect(Aspect.all(FusedBombComponent::class)).isEmpty()) return
        // FusedBombComponent still present — BombFuseSystem will process and remove it.
        val board = boardState() ?: return
        board.phase = GamePhase.RESOLVE
        board.currentSubphase = ResolveSubphase.BOMB
        processWorld()
    }

    fun onLaserAnimationFinished() {
        // Guard: no-op if no activated laser in flight.
        if (world.getEntitiesForAspect(Aspect.all(ActivatedLaserComponent::class)).isEmpty()) return
        // ActivatedLaserComponent still present — LaserResolveSystem will process and remove it.
        val board = boardState() ?: return
        board.phase = GamePhase.RESOLVE
        board.currentSubphase = ResolveSubphase.LASER
        processWorld()
    }

    fun onEffectsAnimationFinished() {
        // Guard: no-op if no exploding entity in flight.
        if (world.getEntitiesForAspect(Aspect.all(ExplodingComponent::class)).isEmpty()) return
        // ExplodingComponent still present — EffectResolveSystem will process and remove it.
        val board = boardState() ?: return
        board.phase = GamePhase.RESOLVE
        board.currentSubphase = ResolveSubphase.EFFECT
        processWorld()
    }

    fun onSwirlVacuumAnimationFinished() {
        // Guard: no-op if no vacuum targets remain (e.g. drainAnimations calls multiple callbacks).
        val vacuumEntities = world.getEntitiesForAspect(Aspect.all(SwirlVacuumComponent::class))
        if (vacuumEntities.isEmpty()) return
        val board = boardState() ?: return
        val swirlVacuumMapper: ComponentMapper<SwirlVacuumComponent> = world.mapper()
        val swirlIds = vacuumEntities.mapNotNull { swirlVacuumMapper[it]?.swirlEntityId }.toSet()
        world.scoreDeleteAndCheckGoals(vacuumEntities.toSet(), board)
        swirlIds.forEach { world.deleteEntity(it) }
        // Route to GRAVITY so the board fills the holes left by deleted jellies before the next
        // RESOLVE cycle runs match detection.
        board.phase = GamePhase.GRAVITY
        processWorld()
    }

    private fun removeAllOf(cls: kotlin.reflect.KClass<out Component>) {
        world.getEntitiesForAspect(Aspect.all(cls)).toList()
            .forEach { world.removeComponent(it, cls) }
    }

    fun hasValidMove(): Boolean = world.hasValidMove(boardWidth(), boardHeight())

    fun finalizeLevelLoss() {
        if (lossFinalized || levelConfig == null || isTestLevel || boardState()?.won == true) return
        // E19 seam: the continue-offer flow should sit before this finalization call.
        livesService?.consumeLife()
        lossFinalized = true
    }

    fun finalizeQuitToJourney() {
        if (levelConfig != null && !isTestLevel && firstMoveMade && boardState()?.won != true) {
            finalizeLevelLoss()
        }
    }

    /**
     * The first adjacent pair of regular (non-special) draggable jellies, in raster
     * scan order (row-major, left-to-right then top-to-bottom), whose swap produces a
     * match. Thin wrapper over [World.firstAvailableSwapHint] — the ECS-level pure
     * board inspection that also powers the level-1 tutorial's live highlight (issue
     * #110, computed by `RenderSystem` from `TutorialComponent`). Exposed here mainly
     * for tests; production UI reads the highlight via `state.tutorial`.
     */
    fun firstAvailableSwapHint(): Pair<GridPos, GridPos>? =
        world.firstAvailableSwapHint(boardWidth(), boardHeight(), pieceSet)?.let { (from, to) ->
            GridPos(from.first, from.second) to GridPos(to.first, to.second)
        }

    /**
     * Dismisses the active tutorial for this playthrough — the "Skip" affordance on
     * step 1, or a tap anywhere during step 2 ("Dismiss on tap"). Unlike the original
     * design this does NOT persist "done" anywhere: per issue #110, the tutorial is
     * meant to show on every play of level 1, not just a player's first attempt, so
     * skipping only clears the ECS [TutorialComponent] for the current board. No-op if
     * no tutorial is active.
     */
    fun dismissTutorial() {
        if (!world.hasComponent(boardEntity, TutorialComponent::class)) return
        world.removeComponent(boardEntity, TutorialComponent::class)
        processWorld()
    }

    private fun processWorld() {
        val maxIterations = 64
        var iterations = 0
        while (iterations < maxIterations) {
            world.process()
            val phase = boardState()?.phase ?: break
            iterations++
            if (phase == GamePhase.INPUT || phase == GamePhase.ANIMATE) break
        }
        syncState()
        val board = boardState() ?: return
        if (board.phase == GamePhase.INPUT) {
            persistProgress(board.score, board.won)
            if (board.won) {
                audioPlayer.playSfx(SoundEvent.SWIRL_CLEAR)
                journeyService?.unlockNextLevel(currentJourneyLevelId)
                recordWinStarsOnce(board.score)
                // First-win gem bonus (E18/F18.2). Journey levels only: arcade has no
                // level id and test levels are dev tooling. The call is idempotent, so
                // repeated INPUT frames on a won board pay out at most once.
                if (levelConfig != null && !isTestLevel) {
                    wallet?.awardFirstWinBonus(currentJourneyLevelId)
                }
            } else {
                val nowHasValidMove = hasValidMove()
                if (hadValidMove && !nowHasValidMove) {
                    audioPlayer.playSfx(SoundEvent.GAME_OVER)
                }
                hadValidMove = nowHasValidMove
            }
        }
    }

    private fun persistProgress(score: Int, won: Boolean) {
        if (score > highScore) {
            highScore = score
            storage.highScore = score
        }
        storage.saveSnapshot(world.snapshot())
        if (won && levelConfig != null && !isTestLevel) {
            storage.lifetimeLevelsCompleted += 1
        }
        if (!won) {
            when {
                levelConfig != null ->
                    storage.continueContext = ContinueContext("journey", currentJourneyLevelId)
                initialGrid == null ->
                    storage.continueContext = ContinueContext("arcade")
            }
        }
    }

    private fun syncState() {
        frameCount++
        val animating = boardState()?.phase != GamePhase.INPUT
        val componentNames = buildSelectedEntityComponentList()

        if (!enableUiFlights) {
            deleteAllFlightEntities()
        }

        val currentPhase = renderSystem.gameState.debugInfo.phase
        if (currentPhase != lastTrackedPhase) {
            phaseHistory.addLast(currentPhase)
            if (phaseHistory.size > 10) phaseHistory.removeFirst()
            lastTrackedPhase = currentPhase
        }

        state = renderSystem.gameState.copy(
            debugInfo = renderSystem.gameState.debugInfo.copy(
                frameCount = frameCount,
                isAnimatingFlag = animating,
                phaseHistory = phaseHistory.toList(),
                showDebugOverlay = showDebugOverlay,
            ),
            selectedEntityComponents = componentNames,
            isJourneyMode = journeyService != null,
            isLastJourneyLevel = journeyService?.isLastLevel(currentJourneyLevelId) ?: false,
            isTestLevel = isTestLevel,
            inputMode = inputMode,
            teleportLinkingDestination = teleportLinkingDestination,
            starsEarned = if (renderSystem.gameState.won && journeyService != null) lastWinStars else 0,
            isNewStarBest = renderSystem.gameState.won && journeyService != null && lastWinIsNewBest,
        )
        isAnimating = animating
    }

    private fun deleteAllFlightEntities() {
        world.getEntitiesForAspect(Aspect.all(FlyingSpriteComponent::class))
            .toList()
            .forEach { world.deleteEntity(it) }
        world.getEntitiesForAspect(Aspect.all(FamiliarFlightComponent::class))
            .toList()
            .forEach { world.deleteEntity(it) }
        world.getEntitiesForAspect(Aspect.all(BombFlightComponent::class))
            .toList()
            .forEach { world.deleteEntity(it) }
    }

    private fun buildSelectedEntityComponentList(): List<String> {
        val sel = renderSystem.gameState.selected ?: return emptyList()
        val entity = findEntityAt(sel.row, sel.col) ?: return listOf("(no entity)")
        return world.getAllComponentsForEntity(entity).map { component ->
            val name = component::class.simpleName ?: component.toString()
            when (component) {
                is GridPositionComponent -> "GridPos(${component.row},${component.col})"
                is JellyTypeComponent -> "JellyType(${component.type})"
                is BodyImageComponent -> "BodyImage(${component.image})"
                is JellyFaceComponent -> "JellyFace(${component.image})"
                is BombFaceComponent -> "BombFace(${component.image})"
                is IceCubeComponent -> "IceCube(life=${component.life})"
                is BoardStateComponent -> "BoardState(${component.phase})"
                is SwappingComponent -> "Swapping"
                is FallingComponent -> "Falling((${component.fromRow},${component.fromCol})->(${component.toRow},${component.toCol}))"
                is GhostBombComponent -> "Ghost(${component.row},${component.col})"
                is FlyingSpriteComponent -> "FlyingSprite"
                is FamiliarFlightComponent -> "FamiliarFlight"
                is BombFlightComponent -> "BombFlight"
                is ScreenTransformComponent -> "ScreenTransform"
                else -> name.removeSuffix("Component")
            }
        }.sorted()
    }

    private fun boardState(): BoardStateComponent? =
        world.getComponent(boardEntity, BoardStateComponent::class)

    private fun boardWidth(): Int = boardState()?.width ?: GRID_SIZE

    private fun boardHeight(): Int = boardState()?.height ?: GRID_SIZE

    /**
     * Derives the board dimensions (width × height = columns × rows) for a freshly
     * created board. A seeded grid dictates its own size; a journey level may declare
     * one; otherwise the default [GRID_SIZE] square is used.
     */
    private fun resolveInitialDimensions(): Pair<Int, Int> {
        val seeded = initialGrid
        return when {
            seeded != null -> {
                val height = seeded.size
                val width = seeded.firstOrNull()?.size ?: 0
                require(width > 0 && height > 0) { "Initial grid must be non-empty" }
                require(seeded.all { it.size == width }) { "Initial grid rows must all have width $width" }
                width to height
            }
            levelConfig != null -> (levelConfig.width ?: GRID_SIZE) to (levelConfig.height ?: GRID_SIZE)
            else -> GRID_SIZE to GRID_SIZE
        }
    }

    private fun initializeGrid() {
        val seededGrid = initialGrid
        val width = boardWidth()
        val height = boardHeight()

        // Reset board-scoped layout state so a fresh call (forceNewGame / resetBoard)
        // does not accumulate holes or teleport markers from a previous round.
        boardState()?.let { existing ->
            world.addComponent(boardEntity, existing.copy(holes = emptySet()))
        }
        world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, TeleportTargetMarkerComponent::class),
        ).toList().forEach { world.deleteEntity(it) }
        // Terrain tiles (E5) survive clearGrid (no JellyTypeComponent), so delete them
        // here too before re-seeding — same lifecycle as teleport markers.
        world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, VoidPuddleTerrainComponent::class),
        ).toList().forEach { world.deleteEntity(it) }
        world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, PurifiedRuneComponent::class),
        ).toList().forEach { world.deleteEntity(it) }

        // Apply hole sentinels from the seeded grid to BoardStateComponent.holes
        // before the cell-creation loop, so `world.isHole` correctly skips them.
        if (seededGrid != null) {
            val board = boardState()
            if (board != null) {
                val holesFromGrid = mutableSetOf<Pair<Int, Int>>()
                for (row in 0 until height) {
                    val rowList = seededGrid[row]
                    require(rowList.size == width) { "Initial grid rows must all have width $width" }
                    for (col in 0 until width) {
                        if (rowList[col] == GridTokens.HOLE) holesFromGrid += row to col
                    }
                }
                if (holesFromGrid.isNotEmpty()) {
                    world.addComponent(boardEntity, board.copy(holes = board.holes + holesFromGrid))
                }
            }
        }

        // For random maps, drill one teleport channel into the board: a hole somewhere
        // in the interior with the entrance directly above it and the exit directly
        // below it. The hole row is constrained so both endpoints stay on the board.
        var randomTeleport: TeleportLinkSpec? = null
        if (seededGrid == null) {
            val board = boardState()
            if (board != null) {
                val holeRow = random.nextInt(1, height - 1)
                val holeCol = random.nextInt(width)
                world.addComponent(boardEntity, board.copy(holes = board.holes + (holeRow to holeCol)))
                randomTeleport = TeleportLinkSpec(
                    srcRow = holeRow - 1, srcCol = holeCol,
                    destRow = holeRow + 1, destCol = holeCol,
                )
            }
        }

        do {
            clearGrid()
            for (row in 0 until height) {
                for (col in 0 until width) {
                    if (world.isHole(row, col)) continue
                    if (seededGrid != null) {
                        val token = seededGrid[row][col]
                        if (token != GridTokens.HOLE) createEntitiesForToken(row, col, token)
                    } else {
                        world.createRandomBoardEntity(row = row, col = col, random = random, catalog = catalog)
                    }
                }
            }
            if (seededGrid != null) return
            eliminateInitialMatches()
            repeat(levelConfig?.iceCount ?: 1) { placeIceCube() }
        } while (!hasValidMove())
        // Auto-create invisible above-board spawn points so SpawnSystem replenishes
        // jellies from the top Ă˘â‚¬â€ť the classic "jellies fall from top" behaviour for
        // randomly generated maps. On-board spawn points placed via the editor
        // override this for specific columns (SpawnSystem handles both).
        for (col in 0 until width) {
            world.createSpawnPointEntity(row = -1, col = col, visible = false)
        }
        randomTeleport?.let { spec ->
            world.createTeleportTargetEntity(
                destRow = spec.destRow, destCol = spec.destCol,
                sourceRow = spec.srcRow, sourceCol = spec.srcCol,
            )
        }
        applyGoals(levelConfig)
        ensureFamiliarEntity()
        applyMoveLimit(levelConfig)
        initializePowerUps()
    }

    /**
     * Creates the entity/entities encoded by one seeded-grid [token] at ([row], [col]).
     * Tokens compose left-to-right: an optional terrain prefix (`"void_puddle"` /
     * `"purified_rune"`, standing alone for an empty terrain cell or followed by `:`
     * and a piece token), then an optional [GridTokens.CURSED_PREFIX], then the piece
     * token itself. Examples: `"void_puddle"`, `"cursed:moon_crystal"`,
     * `"purified_rune:cursed:moon_crystal"`.
     */
    private fun createEntitiesForToken(row: Int, col: Int, token: String) {
        var rest = token
        when {
            rest == PieceIds.VOID_PUDDLE -> {
                world.createVoidPuddleEntity(row = row, col = col)
                return
            }
            rest == PieceIds.PURIFIED_RUNE -> {
                world.createPurifiedRuneEntity(row = row, col = col)
                return
            }
            rest.startsWith("${PieceIds.VOID_PUDDLE}:") -> {
                world.createVoidPuddleEntity(row = row, col = col)
                rest = rest.removePrefix("${PieceIds.VOID_PUDDLE}:")
            }
            rest.startsWith("${PieceIds.PURIFIED_RUNE}:") -> {
                world.createPurifiedRuneEntity(row = row, col = col)
                rest = rest.removePrefix("${PieceIds.PURIFIED_RUNE}:")
            }
        }
        val cursed = rest.startsWith(GridTokens.CURSED_PREFIX)
        if (cursed) rest = rest.removePrefix(GridTokens.CURSED_PREFIX)
        val entityId = when (rest) {
            GridTokens.EMPTY -> null
            GridTokens.ICE_CUBE_LIFE_1 -> world.createIceCubeEntity(row = row, col = col, life = 1)
            PieceIds.SPAWN_POINT -> world.createSpawnPointEntity(row = row, col = col)
            PieceIds.COLOURFUL_SWIRL ->
                world.createColourfulSwirlEntity(row = row, col = col, jellyType = random.nextInt(1, 7))
            else -> world.createPieceEntity(
                row = row, col = col,
                pieceId = rest, pieceSet = pieceSet, random = random,
            )
        }
        if (cursed && entityId != null) world.addComponent(entityId, CursedComponent())
    }

    private fun applyMoveLimit(config: LevelConfig?) {
        val boardId = boardEntity
        world.removeComponent(boardId, MoveLimitComponent::class)
        val limit = config?.moveLimit ?: return
        if (limit <= 0) return
        world.addComponent(boardId, MoveLimitComponent(totalMoves = limit, remainingMoves = limit))
    }

    private fun initializePowerUps() {
        world.getEntitiesForAspect(Aspect.all(PowerUpTypeComponent::class))
            .toList()
            .forEach { world.deleteEntity(it) }
        inputMode = InputMode.SWAP
        rowBombInFlight = false
        val bombUses = levelConfig?.bombUses ?: 1  // null = free-play gets 1 by default
        if (bombUses > 0) {
            world.createPowerUpEntityByPieceId(PieceIds.POWERUP_BOMB, uses = bombUses)
        }
        val rowBombUses = levelConfig?.rowBombUses ?: 1
        if (rowBombUses > 0) {
            world.createPowerUpEntityByPieceId(PieceIds.POWERUP_ROW_BOMB, uses = rowBombUses)
        }
        val targetedRemoveUses = levelConfig?.targetedRemoveUses ?: 1
        if (targetedRemoveUses > 0) {
            world.createPowerUpEntityByPieceId(PieceIds.POWERUP_TARGETED_REMOVE, uses = targetedRemoveUses)
        }
    }

    private fun applyGoals(config: LevelConfig?) {
        applyGoalConfigs(config?.goals)
    }

    /**
     * Installs the level's collection goals. When [goals] is null or empty a random
     * "collect 10" goal is generated (arcade / unconfigured test levels); otherwise the
     * supplied objectives are used verbatim.
     */
    private fun applyGoalConfigs(goals: List<LevelGoalConfig>?) {
        val boardId = boardEntity
        world.removeComponent(boardId, GoalsComponent::class)
        val goalList = if (!goals.isNullOrEmpty()) {
            goals.map {
                Goal(
                    pieceId = it.pieceId,
                    targetQuantity = it.target,
                    remainingQuantity = it.target,
                )
            }.toMutableList()
        } else {
            val jellyTypes = catalog.bodyTypeMap.values.filter { it > 0 }.distinct()
            val goalType = jellyTypes.random(random)
            mutableListOf(Goal(pieceId = pieceIdForJellyType(goalType), targetQuantity = 10, remainingQuantity = 10))
        }
        world.addComponent(boardId, GoalsComponent(goalList))
        boardState()?.won = false
    }

    private fun placeIceCube() {
        repeat(20) {
            val row = random.nextInt(boardHeight())
            val col = random.nextInt(boardWidth())
            if (world.isHole(row, col)) return@repeat
            world.findEntityAt(row, col)?.let { world.deleteEntity(it) }
            world.createIceCubeEntity(row = row, col = col, life = 3)
            return
        }
    }

    private fun clearGrid() {
        val aspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class)
        world.getEntitiesForAspect(aspect).forEach { world.deleteEntity(it) }
    }

    private fun ensureFamiliarEntity(type: FamiliarType = storage.selectedFamiliar) {
        val (familiarType, threshold, trigger) = familiarSpecFor(type)
        val existing = world.getEntitiesForAspect(Aspect.all(FamiliarComponent::class)).firstOrNull()
        val component = FamiliarComponent(
            progress = 0,
            threshold = threshold,
            type = familiarType,
            triggerPieceId = trigger,
        )
        if (existing != null) {
            world.addComponent(existing, component)
        } else {
            world.createFamiliarEntity(threshold = threshold, type = familiarType, triggerPieceId = trigger)
        }
    }

    /**
     * Resolves a [FamiliarType] into its (type, threshold, triggerPieceId) configuration.
     * The Conjurer triggers on the level's primary goal ingredient after 12 collected; the
     * Black Cat (the default, and the fallback for not-yet-implemented types) counts every
     * goal ingredient toward a threshold of 10.
     */
    private fun familiarSpecFor(type: FamiliarType): Triple<FamiliarType, Int, String?> = when (type) {
        FamiliarType.CONJURER -> Triple(
            FamiliarType.CONJURER,
            12,
            world.goalsComponent()?.goals?.firstOrNull()?.pieceId,
        )
        else -> Triple(FamiliarType.BLACK_CAT, 10, null)
    }

    private fun ensureBoardConfiguration(): BoardSpawnControlComponent {
        if (world.getComponent(boardEntity, PieceSetIdComponent::class) == null) {
            world.addComponent(boardEntity, PieceSetIdComponent(pieceSet.id))
        }
        val existing = world.getComponent(boardEntity, BoardSpawnControlComponent::class)
        if (existing != null) return existing
        return BoardSpawnControlComponent().also { world.addComponent(boardEntity, it) }
    }

    private fun eliminateInitialMatches() =
        world.eliminateInitialMatches(boardWidth(), boardHeight(), catalog, random)

    private fun findEntityAt(row: Int, col: Int): Int? = world.findEntityAt(row, col)

    private fun clearMoveHistory() {
        moveHistory.clear()
        syncMoveHistoryFlags()
    }

    private fun syncMoveHistoryFlags() {
        canUndoMove = moveHistory.canUndo
        canRedoMove = moveHistory.canRedo
    }

    private fun recordStateBeforePlayerMove() {
        moveHistory.record()
        syncMoveHistoryFlags()
    }

    private fun decrementMoveLimit() {
        val limit = world.getComponent(boardEntity, MoveLimitComponent::class) ?: return
        limit.remainingMoves -= 1
    }

    private fun isLost(): Boolean {
        val limit = world.getComponent(boardEntity, MoveLimitComponent::class) ?: return false
        val board = boardState() ?: return false
        return limit.remainingMoves <= 0 && !board.won
    }

    /**
     * Computes and persists the star rating for a journey win exactly once per win
     * (idempotent across the repeated INPUT ticks the win dialog stays open for),
     * then refreshes [state] so the dialog shows the earned stars / new-best flag.
     * No-op outside journey mode or after the win has already been recorded.
     */
    private fun recordWinStarsOnce(score: Int) {
        val svc = journeyService ?: return
        val cfg = levelConfig ?: return
        if (winStarsRecorded) return
        winStarsRecorded = true
        lastWinStars = svc.starsForWin(score, cfg)
        lastWinIsNewBest = svc.recordLevelStars(currentJourneyLevelId, lastWinStars)
        syncState()
    }
}
